import { isProduction } from '@leagueplatform/app-environment';
import { captureError } from '@leagueplatform/observability';

export type BFCachePhase = 'freeze' | 'restore';

export const DEFAULT_BFCACHE_HANDLER_TIMEOUT_MS = 5000;

type ObservabilityOptions = {
  label: string;
  phase: BFCachePhase;
  timeoutMs?: number;
  extraTags?: Record<string, string>;
};

/**
 * Wraps a BFCache handler with timeout enforcement and error observability.
 * Errors are caught, sent to observability, and suppressed (not re-thrown) to
 * prevent restore handlers from crashing the page.
 * This helper is primarily intended for internal SDK usage and the additional side
 * effects should be considered when using it externally.
 */
export const withBFCacheObservability =
  <T extends Event>(
    handler: (event: T, signal?: AbortSignal) => void | Promise<void>,
    {
      label,
      phase,
      timeoutMs = DEFAULT_BFCACHE_HANDLER_TIMEOUT_MS,
      extraTags,
    }: ObservabilityOptions,
  ) =>
  async (event: T) => {
    // Create AbortController for timeout
    const controller = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        controller.abort();
        reject(
          new Error(
            `BFCache handler '${label}' timed out after ${timeoutMs}ms`,
          ),
        );
      }, timeoutMs);
    });

    try {
      // Race handler execution against timeout
      await Promise.race([
        Promise.resolve(handler(event, controller.signal)),
        timeoutPromise,
      ]);
    } catch (error) {
      captureError(error instanceof Error ? error : new Error(String(error)), {
        tags: {
          phase,
          label,
          ...extraTags,
        },
      });

      if (!isProduction()) {
        // eslint-disable-next-line no-console -- surfaced only in dev/staging for diagnostics
        console.error(
          `[BFCacheEffect][${phase}] handler (${label}) failed`,
          error,
        );
      }
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }
  };
